'use client';

import {
  AttributeSide,
  AttributeSideDynamic,
} from '@eliadmin/core/src/components/categories/attributes/attribute';
import { AttributeCategory } from '@eliadmin/core/src/components/categories/attributes/attribute-category';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { CATEGORY_CONFIG } from '@eliadmin/core/src/constants/category-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { FiArrowLeft } from 'react-icons/fi';
import { IoClose, IoOptionsSharp } from 'react-icons/io5';
import { RiBrush3Line } from 'react-icons/ri';

const FilterSeparator = ({ className }: { className?: string }) => (
  <Separator className={cn('mx-4 w-auto last-of-type:hidden sm:mx-6 lg:mx-8', className)} />
);

export function CategoryFilterSheet() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const categoryTree = useCategoryStore(state => state.categoryTree);
  const category = useCategoryStore(state => state.category);
  const { t } = useTranslations();
  const clearFilter = useCategoryStore(s => s.clearFilters);

  return (
    <Sheet>
      <SheetTrigger className="flex h-12 items-center justify-center gap-3 border border-border bg-white px-6 text-sm font-medium tracking-tighter text-black md:text-base lg:h-14">
        <IoOptionsSharp className="h-5 w-5 -rotate-90 text-black" /> {t('text174')}
      </SheetTrigger>
      <SheetContent className="w-full px-0 py-0 sm:max-w-[505px]" showCloseIcon={false}>
        <Card
          className={cn(
            'relative h-screen max-h-screen overflow-auto bg-background pt-2',
            'ea-category-filter-sheet',
          )}
        >
          <div
            className={cn(
              'flex w-full flex-row items-center justify-between px-4 pb-4 pt-4 sm:px-6 sm:pt-6 lg:px-8',
              'ea-category-filter-sheet__expand-button',
            )}
          >
            <SheetTitle className="text-2xl font-normal tracking-tighter text-black lg:text-3xl">
              {t('text174')}
            </SheetTitle>
            <SheetClose
              type="button"
              aria-label="Zavrieť filter"
              className="inline-flex shrink-0 items-center justify-center p-0"
            >
              <IoClose
                aria-hidden="true"
                className="h-5 w-5 opacity-50 transition-all hover:opacity-75 sm:h-6 sm:w-6"
              />
            </SheetClose>
            {/* add data expanded attribute */}
          </div>

          <div className={cn('flex flex-col gap-4', 'ea-category-filter-sheet__content')}>
            <FilterSeparator />
            {/* categories */}
            <div className="block w-full sm:hidden">
              {' '}
              {!!templateSettings.category?.tree_list?.enabled && !!categoryTree && (
                <AttributeCategory order={templateSettings.category.tree_list.order} />
              )}
              <FilterSeparator />
              {!!templateSettings.category?.categories_in_filter?.enabled &&
                !!category.sub_categories?.length && (
                  <>
                    <AttributeSide
                      name={t('text337')}
                      type="subCategory"
                      attribute={{ type: 'status', type2: 'statuses' }}
                      order={templateSettings.category.categories_in_filter.order}
                      className={{ wrapper: cn('', 'ea-category-filter-sheet__subcategories') }}
                    />
                  </>
                )}
            </div>
            <FilterSeparator className="block sm:hidden" />
            {/* manufacturers */}
            {!!templateSettings.category?.manufacturers_in_filter?.enabled &&
              !!category.manufacturers?.length && (
                <>
                  <AttributeSide
                    name={t('text195')}
                    type="generic"
                    attribute={{ type: 'manufacturer', type2: 'manufacturers' }}
                    order={templateSettings.category.manufacturers_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__manufacturers') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* statuses */}
            {!!templateSettings.category?.statuses_in_filter?.enabled &&
              !!category.statuses?.length && (
                <>
                  <AttributeSide
                    name={t('text518')}
                    type="generic"
                    attribute={{ type: 'status', type2: 'statuses' }}
                    order={templateSettings.category?.categories_in_filter?.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__statuses') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* availabilities */}
            {!!templateSettings.category?.availability_in_filter?.enabled &&
              !!category.availabilities?.length && (
                <>
                  <AttributeSide
                    name={t('text339')}
                    type="generic"
                    attribute={{ type: 'availability', type2: 'availabilities' }}
                    order={templateSettings.category.availability_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__availabilities') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* attributes */}
            {!!templateSettings.category?.attributes_in_filter?.enabled && (
              // <div className={`order-${templateSettings.category.attributes_in_filter.order}`}>
              <>
                {category.attributes?.map((attribute, i) => (
                  <>
                    <AttributeSideDynamic
                      key={`attribute-${attribute.id ?? attribute.name}-${i}`}
                      name={attribute.name}
                      attribute={attribute as any}
                      className={{ wrapper: cn('', 'ea-category-filter-sheet__attributes') }}
                    />
                    <FilterSeparator />
                  </>
                ))}
              </>
            )}

            {/* price */}
            {!!templateSettings.category?.price_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text340')}
                  type="price"
                  attribute={{ type: 'status', type2: 'statuses' }}
                  order={templateSettings.category.price_in_filter.order}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__price') }}
                />
                <FilterSeparator />
              </>
            )}

            {!!templateSettings.category?.suppliers_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text718')}
                  type="generic"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers') }}
                  order={templateSettings.category.suppliers_in_filter.order}
                />
                <FilterSeparator />
              </>
            )}
            {!!templateSettings.category?.suppliers_location_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text728')}
                  type="supplierLocation"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers_location') }}
                  order={templateSettings.category?.suppliers_in_filter?.order}
                />
                <FilterSeparator />
              </>
            )}
          </div>

          <div className="sticky bottom-0 flex flex-1 flex-col justify-end gap-2 bg-background pb-2">
            <Separator />
            <div className="flex w-full items-center justify-center gap-2 px-4 sm:px-6 lg:px-8">
              <Button className="w-full whitespace-nowrap">{t('text634')}</Button>
              <span
                onClick={clearFilter}
                className="flex h-12 min-w-12 items-center justify-center border border-border text-black lg:h-14 lg:min-w-14"
              >
                <RiBrush3Line size={20} />
              </span>
            </div>
          </div>
        </Card>
      </SheetContent>
    </Sheet>
  );
}
